import type { ColorType } from "@UI/types/colors.ts";
import type { IconNameType } from "@UI/types/icons.ts";
import type { NotificationState } from "@UI/types/notifications.ts";
import type { FC, MouseEventHandler, ReactNode } from "react";
import DClearButton from "@UI/components/Atoms/Button/DClearButton.tsx";
import DIcon from "@UI/components/Atoms/Icon/DIcon.tsx";
import classNames from "classnames";

interface Props {
  label?: ReactNode;
  description?: ReactNode;
  size?: "small" | "medium" | "large";
  state?: NotificationState;
  style?: "inline" | "floating";
  action?: ReactNode;
  onDismiss?: MouseEventHandler;
  /** Render `label` as bare custom content — no state icon, no text wrapper. */
  plain?: boolean;
}

const STATE_CONFIG: Record<
  NotificationState,
  { icon: IconNameType; iconColor: ColorType }
> = {
  error: { icon: "error", iconColor: "error" },
  warning: { icon: "warning", iconColor: "warning" },
  success: { icon: "check-circle", iconColor: "success" },
  info: { icon: "info", iconColor: "info" },
  neutral: { icon: "question-mark-help", iconColor: "label" },
};

const DNotification: FC<Props> = ({
  label,
  description,
  size = "small",
  state = "neutral",
  style = "inline",
  action,
  onDismiss,
  plain = false,
}) => {
  const classes = classNames(
    "d-notification",
    `d-notification--${size}`,
    style === "floating"
      ? "d-notification--floating"
      : `d-notification--${state}`,
  );

  const isLarge = size === "large";
  const role = state === "error" || state === "warning" ? "alert" : "status";

  if (plain) {
    return (
      <div className={classes} role={role}>
        <div className="d-flex-1 d-min-w-0">{label}</div>
        {onDismiss && <DClearButton onClick={onDismiss} />}
      </div>
    );
  }

  return (
    <div className={classes} role={role}>
      <DIcon
        name={STATE_CONFIG[state].icon}
        color={STATE_CONFIG[state].iconColor}
      />
      {isLarge ? (
        <div className="d-flex-column d-flex-1 d-gap-10 d-min-w-0">
          <div className="d-flex-column d-gap-4">
            {label && (
              <span className="d-label-s-medium d-text-black">{label}</span>
            )}
            {description && (
              <span className="d-paragraph-s-regular d-text-light">
                {description}
              </span>
            )}
          </div>
          {action && action}
        </div>
      ) : (
        <>
          {label && (
            <span
              className={classNames(
                size === "small" ? "d-paragraph-x-regular" : "d-label-s-medium",
                "d-text-black d-flex-1 d-min-w-0",
              )}
            >
              {label}
            </span>
          )}
          {action && action}
        </>
      )}
      {onDismiss && <DClearButton onClick={onDismiss} />}
    </div>
  );
};

export default DNotification;
